<template>
  <v-sheet class="optionProductItem mx-4 mx-md-8 mb-3 px-4 py-2 rounded-10" outlined rounded>
    <div class="d-flex align-center justify-space-between">
      <div class="flex-grow-1">
        <div class="align-center">
          <div class="text-md-h6 text-body-1 font-weight-normal" style="color: #535353">{{ getTitle() }}</div>
          <div class="text-md-h6 text-body-1 font-weight-normal mr-4 warning--text" style="margin-top: -4px">
            <template v-if="isMultipleType && selected">
              {{ $toComma(totalPrice) }}<span style="color: #535353" class="ml-1">{{ $t("common.krw") }}</span>
              <span style="color: #999; font-size: 0.9em" class="ml-1">({{ $toComma(product.price) }} x {{ quantity }})</span>
            </template>
            <template v-else>
              {{ $toComma(product.price) }}<span style="color: #535353" class="ml-1">{{ $t("common.krw") }}</span>
            </template>
          </div>
        </div>
      </div>
      <div class="d-flex align-center">
        <v-btn v-if="product.descriptionCode" depressed class="mr-6 primary" style="font-size: 16px" @click="showDetailDialog">
          {{ $t("common.detail") }}
        </v-btn>

        <!-- multiple 타입일 때 수량 선택 -->
        <div v-if="isMultipleType && selected" class="d-flex align-center mr-4">
          <v-btn icon small @click="decreaseQuantity" :disabled="quantity <= 1">
            <v-icon>mdi-minus</v-icon>
          </v-btn>
          <span class="mx-2 text-h6" style="min-width: 30px; text-align: center">{{ quantity }}</span>
          <v-btn icon small @click="increaseQuantity" :disabled="quantity >= maxQuantity">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </div>

        <v-checkbox v-model="selected" color="primary" hide-details @change="onSelectChange" class="ma-0 pa-0" style="transform: scale(1.3)"></v-checkbox>
      </div>
    </div>

    <!-- 상세 내용 다이얼로그 -->
    <v-dialog v-model="detailDialog" max-width="540" content-class="rounded-10" style="z-index: 15">
      <v-card>
        <v-card-title>{{ getTitle() }}</v-card-title>
        <v-card-text>
          <v-sheet outlined style="min-height: 300px; max-height: 500px; overflow-y: auto; background-color: #fafafa" class="pa-2">
            <div v-if="descriptionLoading" class="text-center py-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
            <div v-else-if="descriptionContent" v-html="getDescriptionContent()" class="description-content"></div>
            <div v-else class="text-center py-8 grey--text text-body-1">{{ $t("common.error_load_detail") }}</div>
          </v-sheet>
        </v-card-text>
        <v-card-actions class="d-flex justify-center">
          <v-btn color="black" outlined class="dialog_btn rounded-2" style="font-size: 16px" width="35%" depressed @click="detailDialog = false" large>
            {{ $t("button.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-sheet>
</template>

<script>
import axios from "@/api/restApi"

export default {
  name: "PatternOptionProductItem",
  props: {
    product: {
      type: Object,
      required: true,
    },
    value: {
      type: Boolean,
      default: false,
    },
    patternCount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      selected: this.value,
      quantity: 1,
      detailDialog: false,
      descriptionContent: null,
      descriptionLoading: false,
    }
  },
  computed: {
    isMultipleType() {
      return this.product.type === "multiple"
    },
    maxQuantity() {
      // patternCount가 전달되면 해당 값 사용, 아니면 product.maxCount 또는 기본값
      return this.patternCount > 0 ? this.patternCount : this.product.maxCount || 19
    },
    totalPrice() {
      return this.product.price * this.quantity
    },
  },
  watch: {
    value(newVal) {
      this.selected = newVal
      if (!newVal) {
        this.quantity = 1
      }
    },
    detailDialog(val) {
      if (val && !this.descriptionContent && this.product.descriptionCode) {
        this.loadDescription()
      }
    },
    quantity() {
      if (this.selected && this.isMultipleType) {
        this.onSelectChange()
      }
    },
  },
  methods: {
    getTitle() {
      if (!this.product || !this.product.title) return ""

      // title이 문자열인 경우 JSON 파싱 시도
      if (typeof this.product.title === "string") {
        try {
          const parsed = JSON.parse(this.product.title)
          switch (this.$store.state.lang) {
            case "en":
              return parsed.en || parsed.kr || ""
            case "jp":
              return parsed.jp || parsed.kr || ""
            default:
              return parsed.kr || ""
          }
        } catch {
          return this.product.title
        }
      }

      // title이 객체인 경우
      if (typeof this.product.title === "object") {
        switch (this.$store.state.lang) {
          case "en":
            return this.product.title.en || this.product.title.kr || ""
          case "jp":
            return this.product.title.jp || this.product.title.kr || ""
          default:
            return this.product.title.kr || ""
        }
      }

      return ""
    },

    getSubTitle() {
      if (!this.product || !this.product.subTitle) return ""

      // subTitle이 문자열인 경우 JSON 파싱 시도
      if (typeof this.product.subTitle === "string") {
        try {
          const parsed = JSON.parse(this.product.subTitle)
          switch (this.$store.state.lang) {
            case "en":
              return parsed.en || parsed.kr || ""
            case "jp":
              return parsed.jp || parsed.kr || ""
            default:
              return parsed.kr || ""
          }
        } catch {
          return this.product.subTitle
        }
      }

      // subTitle이 객체인 경우
      if (typeof this.product.subTitle === "object") {
        switch (this.$store.state.lang) {
          case "en":
            return this.product.subTitle.en || this.product.subTitle.kr || ""
          case "jp":
            return this.product.subTitle.jp || this.product.subTitle.kr || ""
          default:
            return this.product.subTitle.kr || ""
        }
      }

      return ""
    },

    onSelectChange() {
      this.$emit("input", this.selected)
      this.$emit("change", {
        product: this.product,
        selected: this.selected,
        quantity: this.quantity,
      })
    },

    increaseQuantity() {
      if (this.quantity < this.maxQuantity) {
        this.quantity++
      }
    },

    decreaseQuantity() {
      if (this.quantity > 1) {
        this.quantity--
      }
    },

    showDetailDialog() {
      this.detailDialog = true
    },

    async loadDescription() {
      if (!this.product.descriptionCode) return

      this.descriptionLoading = true
      try {
        const response = await axios.get("/api/event/select/code", {
          params: { code: this.product.descriptionCode },
        })

        if (response.data && response.data.length > 0) {
          this.descriptionContent = response.data[0]
        }
      } catch (error) {
        console.error("상세 내용 로드 실패:", error)
        this.descriptionContent = null
      } finally {
        this.descriptionLoading = false
      }
    },

    getDescriptionContent() {
      if (!this.descriptionContent) return ""

      // 언어별 컨텐츠 선택
      switch (this.$store.state.lang) {
        case "en":
          return this.descriptionContent.content_en || this.descriptionContent.content || ""
        case "jp":
          return this.descriptionContent.content_jp || this.descriptionContent.content || ""
        default:
          return this.descriptionContent.content || ""
      }
    },
  },
}
</script>

<style scoped>
.optionProductItem {
  border: 2px solid #f5e4e4;
  transition: all 0.2s ease;
}

.optionProductItem:hover {
  border-color: #ef7fab;
}

::v-deep .v-dialog .v-card {
  border: 3px solid #dadada !important;
  border-radius: 10px;
}

.dialog_btn {
  border: 2px solid #dadada !important;
  font-size: 16px;
}

::v-deep .description-content {
  line-height: 1.6;
}

::v-deep .description-content img {
  max-width: 100%;
  height: auto;
}

::v-deep .description-content iframe {
  max-width: 100%;
}
</style>
